import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { AngBridgeUtil } from "../../../util/ang.bridge.util";
import { LoginStore } from "../../../stores/login.store";
import { ElectionApi, type ElectionHeader } from "./election.api";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { IngestionFormComponent } from "../../data-ingestion/ingestion.form.component";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { AlertService } from "../../../services/alert.service";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../../services/state/state.map";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { IngestionPointCategory } from "../../data-ingestion/data.ingestion.const";
import { IngestionAccess } from "../../data-ingestion/ingestion.access";

@TypeManagerDecorator("s25-ng-election-nominations-page")
@Component({
    selector: "s25-ng-election-nominations-page",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25PipesModule, S25ButtonComponent, IngestionFormComponent, S25RouterLinkDirective],
    template: `
        <div class="c-padding--double election-nominations">
            <h1>Election Nominations</h1>
            @if (!loginStore.loggedIn()) {
                <p>Sign in to view open nominations.</p>
            } @else if (elections.isLoading() || mySubmissions.isLoading() || fillablePoints.isLoading()) {
                <p>Loading…</p>
            } @else if (elections.error() || mySubmissions.error() || fillablePoints.error()) {
                <p>{{ nominationsLoadErrorText() }}</p>
            } @else if (nominationFormIngestionPointId() != null && selectedElectionId() != null) {
                @if (nominationAccessDenied()) {
                    <p class="c-text--error">You do not have permissions to this nomination form.</p>
                } @else {
                    <s25-ng-ingestion-form
                        [ingestionPointId]="nominationFormIngestionPointId()!"
                        [electionId]="selectedElectionId()!"
                        [readOnly]="false"
                        [showFormExitActions]="true"
                        (onSubmit)="onNominationFormSubmit()"
                        (onClose)="onNominationFormClose()"
                    />
                }
            } @else {
                @if (openElectionsWithFillAccess().length === 0) {
                    <p>No elections are open for nominations right now.</p>
                } @else {
                    @if (electionsOpenWithoutNominationSubmission().length) {
                        <h2 class="c-margin-bottom--single">Start a Nomination</h2>
                        <ul class="election-nominations__list c-margin-bottom--double">
                            @for (election of electionsOpenWithoutNominationSubmission(); track election.electionId) {
                                <li class="election-nominations__item">
                                    <strong>{{ election.name }}</strong>
                                    @if (election.descr) {
                                        <div
                                            class="election-nominations__descr"
                                            [innerHTML]="election.descr | safeHTML"
                                        ></div>
                                    }
                                    <div class="c-margin-top--single">
                                        <s25-ng-button [type]="'primary'" [onClick]="openFormFactory(election)"
                                            >Nominate</s25-ng-button
                                        >
                                    </div>
                                </li>
                            }
                        </ul>
                    }
                    @if (electionsOpenWithNominationSubmission().length) {
                        <h2 class="c-margin-bottom--single">Nominations in progress or submitted</h2>
                        <p class="c-margin-bottom--single">
                            You already have a nomination form for these elections. View workflow status, save edits
                            before final submission, and open your forms on
                            <a s25-ng-router-link [stateName]="StateNames.seMyForms" class="aw-link">My Forms</a>.
                        </p>
                        <ul class="election-nominations__list c-margin-bottom--double">
                            @for (election of electionsOpenWithNominationSubmission(); track election.electionId) {
                                <li class="election-nominations__item">
                                    <strong>{{ election.name }}</strong>
                                </li>
                            }
                        </ul>
                    }
                    @if (
                        electionsOpenWithoutNominationSubmission().length === 0 &&
                        electionsOpenWithNominationSubmission().length === 0
                    ) {
                        <p>No elections are available to list.</p>
                    }
                }
            }
        </div>
    `,
    styles: `
        .election-nominations__list {
            list-style: none;
            padding: 0;
        }
        .election-nominations__item {
            margin-bottom: 1rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 0.75rem;
        }
        .election-nominations__item:last-child {
            margin-bottom: 0;
        }
        .election-nominations__descr {
            margin-top: 0.5rem;
        }
    `,
})
export class ElectionNominationsPageComponent {
    readonly loginStore = inject(LoginStore);

    private readonly nominationFormRef = viewChild(IngestionFormComponent);

    protected readonly StateNames = S25States.names;

    constructor() {
        effect((onCleanup) => {
            if (this.nominationFormIngestionPointId() == null) {
                return;
            }
            let deregisterTransitionHook: (() => void) | undefined;
            void AngBridgeUtil.$inject("$transitions").then(($transitions) => {
                deregisterTransitionHook = $transitions.onBefore(
                    { from: S25States.names.seElectionNominations },
                    async (transition: { to: () => { name: string } }) => {
                        if (transition.to().name === S25States.names.seElectionNominations) {
                            return true;
                        }
                        const form = this.nominationFormRef();
                        if (!form?.shouldConfirmDiscardOnLeave()) {
                            return true;
                        }
                        return form.confirmAndDiscardDraft();
                    },
                );
            });
            onCleanup(() => deregisterTransitionHook?.());
        });
    }

    /** Bumps when elections and my-submissions should reload (e.g. after a successful nomination form). */
    private readonly nominationsListVersion = signal(0);

    readonly nominationFormIngestionPointId = signal<number | null>(null);
    readonly selectedElectionId = signal<number | null>(null);
    readonly nominationAccessDenied = signal(false);

    readonly elections = s25Resource({
        if: () => this.loginStore.loggedIn() && this.loginStore.init(),
        params: () => this.nominationsListVersion(),
        loader: () => ElectionApi.getOpenNominations(),
    });

    readonly mySubmissions = s25Resource({
        if: () => this.loginStore.loggedIn() && this.loginStore.init(),
        params: () => this.nominationsListVersion(),
        loader: () => DataIngestionService.getMySubmissions(),
    });

    readonly fillablePoints = s25Resource({
        if: () => this.loginStore.loggedIn() && this.loginStore.init(),
        params: () => this.nominationsListVersion(),
        loader: () =>
            DataIngestionService.getIngestionPoints(
                IngestionPointCategory.ENGAGEMENT,
                undefined,
                false,
                ["electionCandidate"],
                IngestionAccess.IngestionPointAction.FILL,
            ),
    });

    readonly fillableIngestionPointIds = computed(() => {
        const pointIds = new Set<number>();
        for (const point of this.fillablePoints.value() ?? []) {
            const pointId = point.ingestionPointId;
            if (pointId) {
                pointIds.add(pointId);
            }
        }
        return pointIds;
    });

    readonly openElectionsWithFillAccess = computed((): ElectionHeader[] => {
        const fillableIds = this.fillableIngestionPointIds();
        return (this.elections.value() ?? []).filter((election) => {
            const pointId = election.candidateIngestionPointId;
            return pointId && fillableIds.has(pointId);
        });
    });

    readonly activeMySubmissions = computed(() => {
        return this.mySubmissions.value() ?? [];
    });

    private hasActiveSubmissionForElection(election: ElectionHeader): boolean {
        const pointId = election.candidateIngestionPointId;
        if (!pointId) {
            return false;
        }
        return this.activeMySubmissions().some(
            (submission) =>
                submission.ingestionPointId === pointId &&
                (submission.electionId ?? null) === (election.electionId ?? null),
        );
    }

    readonly electionsOpenWithoutNominationSubmission = computed((): ElectionHeader[] => {
        return this.openElectionsWithFillAccess().filter((election) => !this.hasActiveSubmissionForElection(election));
    });

    readonly electionsOpenWithNominationSubmission = computed((): ElectionHeader[] => {
        return this.openElectionsWithFillAccess().filter((election) => this.hasActiveSubmissionForElection(election));
    });

    readonly nominationsLoadErrorText = computed(() => {
        const electionError = this.elections.error();
        const submissionError = this.mySubmissions.error();
        const fillableError = this.fillablePoints.error();
        const parts: string[] = [];
        if (electionError) {
            parts.push(String((electionError as Error)?.message ?? electionError));
        }
        if (submissionError) {
            parts.push(String((submissionError as Error)?.message ?? submissionError));
        }
        if (fillableError) {
            parts.push(String((fillableError as Error)?.message ?? fillableError));
        }
        return parts.join(" ") || "Something went wrong.";
    });

    openFormFactory(election: ElectionHeader) {
        return () => this.openForm(election);
    }

    readonly onNominationFormClose = () => {
        this.returnToNominationsList();
    };

    readonly onNominationFormSubmit = async () => {
        this.returnToNominationsList();
        await AlertService.alert("Thank you! Your form has been submitted.");
    };

    private returnToNominationsList(): void {
        this.nominationFormIngestionPointId.set(null);
        this.selectedElectionId.set(null);
        this.nominationAccessDenied.set(false);
        this.nominationsListVersion.update((version) => version + 1);
    }

    private openForm(election: ElectionHeader) {
        const pointId = election.candidateIngestionPointId;
        if (!pointId || election.electionId == null) {
            return;
        }
        const allowed = this.electionsOpenWithoutNominationSubmission().some(
            (row) => row.electionId === election.electionId,
        );
        if (!allowed) {
            return;
        }
        if (!this.fillableIngestionPointIds().has(pointId)) {
            void AlertService.alert("You do not have permission to fill this nomination form.");
            return;
        }
        this.nominationAccessDenied.set(false);
        this.selectedElectionId.set(election.electionId);
        this.nominationFormIngestionPointId.set(pointId);
    }
}
